import React, { useContext, useEffect } from "react";
import "./Intro.scss";
import { MapContext } from "../../../context/MapContext";
import CROPlogo from "../../../assets/images/logos/CROPlogo.png";
import GEFlogo from "../../../assets/images/logos/GEFlogo.png";
import WBGlogo from "../../../assets/images/logos/WBGlogo.png";
import TNCLogo from "../../../assets/images/logos/TNCLogo.png";
import { Button, Divider, Grid } from "@material-ui/core";
import FisheriesIcon from "../../../assets/images/fisheries_icon.png";
import BeachesIcon from "../../../assets/images/beaches_icon.svg";
import RecFishingIcon from "../../../assets/images/recreational_fishing_icon.svg";
import WildlifeIcon from "../../../assets/images/wildlife_tourism_icon.svg";
import TourismIcon from "../../../assets/images/tourism_icon.png";
import utils from "../../../helpers/utils";
import IntroConfig from "./IntroConfig";
import { ArrowForward } from "@material-ui/icons";

function Intro({ setApp }) {
  const [mapObject] = useContext(MapContext);
  /**
   * Every time this route is accessed, make sure the EEZ layer reflects the correct boundaries
   */
  useEffect(() => {
    const eez = mapObject?.map.findLayerById("eez");

    if (eez) {
      eez.definitionExpression = `Dissolve in (${IntroConfig.countries
        .map((c) => "'" + utils.getCountryForEEZ(c) + "'")
        .join(",")})`;
    }
  }, [mapObject]);

  return (
    <div className="intro">
      <div className="content">
        <p>
          Ocean resources in the Caribbean have the potential to make a much
          greater contribution to poverty reduction and shared prosperity for
          the region’s growing population of 40 million than they do currently,
          and to increase the resilience of people to climate change. The
          Caribbean region has been at the forefront of a movement towards the
          development of the blue economy and is home to a growing number of
          developing states that share the Caribbean Sea and have embraced the
          concept as the centerpiece of future growth strategies.
        </p>
        <p>
          This mapping tool is a dedicated extension of the Mapping Ocean Wealth
          Data Explorer developed specifically to support the Caribbean Regional
          Oceanscape Project (CROP). The objective of CROP is to improve systems
          and put relevant structures in place in an effort to foster a Blue
          Economy and to promote greater consideration of the ecosystem
          functions and services, which the ocean provides for member states.
        </p>
        <p>
          Under CROP, The Nature Conservancy (TNC) used the Mapping Ocean Wealth
          approach to develop ecosystem service models at the scale of the
          Eastern Caribbean. Existing coral reef tourism and fisheries models
          were adapted to the regional scale, and novel methodologies were developed to
          characterize recreational fishing and wildlife viewing in
          participating Eastern Caribbean countries (Dominica, Grenada, Saint
          Kitts and Nevis, Saint Lucia, and Saint Vincent and the Grenadines).
          These data will support these countries in ongoing and future marine
          spatial planning through the direct provision of spatially explicit
          information on their ecosystem service values, particularly relating
          to fisheries and nature-based tourism. Use this data explorer to
          explore the mapped outputs of these models, along with
          country-specific reports and statistics.
        </p>
        <p>
          Read more about this project{" "}
          <a
            href="https://oceanwealth.org/project-areas/caribbean/crop"
            target="_blank"
            rel="noreferrer"
          >
            here
          </a>
          .
        </p>
        <p>
          Visit the Caribbean Science Atlas{" "}
          <a
            href="https://caribbeanscienceatlas.tnc.org"
            target="_blank"
            rel="noreferrer"
          >
            here
          </a>
          .
        </p>
        <p>
          The Organisation of Eastern Caribbean States, in partnership with the
          World Bank is implementing CROP with support from the Global
          Environmental Fund (GEF).
        </p>
      </div>
      <div className="explore-content content">
        <h2>Explore the Apps</h2>
        <div>
          <Grid className="explore-section" container>
            <Grid item xs={2}>
              <img
                src={TourismIcon}
                className="tourismIcon app-icon"
                alt="Coral Reef Recreation and Tourism"
              />
            </Grid>
            <Grid className="app-title" item xs={10}>
              Coral Reef Recreation and Tourism
            </Grid>
            <Grid item xs={12} className="app-description">
              Explore the values of on-reef tourism (e.g., SCUBA and
              snorkelling) in five Eastern Caribbean countries.
            </Grid>
            <Grid item xs={8} />
            <Grid item xs={4}>
              <Button
                className="app-button"
                variant="outlined"
                color="primary"
                onClick={() => setApp("coral-rec-tourism")}
              >
                Explore 
                <ArrowForward />
              </Button>
            </Grid>
          </Grid>
          <Grid className="explore-section" container>
            <Grid item xs={2}>
              <img
                src={BeachesIcon}
                className="beachesIcon app-icon"
                alt="Nature-Dependent Beach Recreation and Tourism"
              />
            </Grid>
            <Grid className="app-title" item xs={10}>
              Nature-Dependent Beach Recreation and Tourism
            </Grid>
            <Grid item xs={12} className="app-description">
              Explore the values of nature-dependent beach tourism in five
              Eastern Caribbean countries.
            </Grid>
            <Grid item xs={8} />
            <Grid item xs={4}>
              <Button
                className="app-button"
                variant="outlined"
                color="primary"
                onClick={() => setApp("beach-rec-tourism")}
              >
                Explore 
                <ArrowForward />
              </Button>
            </Grid>
          </Grid>
          <Grid className="explore-section" container>
            <Grid item xs={2}>
              <img
                src={RecFishingIcon}
                className="app-icon"
                alt="Recreational Fishing"
              />
            </Grid>
            <Grid className="app-title" item xs={10}>
              Recreational Fishing
            </Grid>
            <Grid item xs={12} className="app-description">
              Explore the intensity, distribution, and values of recreational
              charter fishing in five Eastern Caribbean countries.
            </Grid>
            <Grid item xs={8} />
            <Grid item xs={4}>
              <Button
                className="app-button"
                variant="outlined"
                color="primary"
                onClick={() => setApp("rec-fishing")}
              >
                Explore 
                <ArrowForward />
              </Button>
            </Grid>
          </Grid>
          <Grid className="explore-section" container>
            <Grid item xs={2}>
              <img
                src={FisheriesIcon}
                className="fisheriesIcon app-icon"
                alt="Coral Reef Fisheries"
              />
            </Grid>
            <Grid className="app-title" item xs={10}>
              Coral Reef Fisheries
            </Grid>
            <Grid item xs={12} className="app-description">
              This app demonstrates the impact of human populations on fish
              stocks and supports fisheries management by helping to identify
              areas that are most likely to benefit from improved management. We
              include data from the five CROP countries as well as adjacent
              island nations included in the model to account for connectivity
              among fish populations.
            </Grid>
            <Grid item xs={8} />
            <Grid item xs={4}>
              <Button
                className="app-button"
                variant="contained"
                color="primary"
                onClick={() => setApp("coral-reef-fisheries")}
              >
                Explore 
                <ArrowForward />
              </Button>
            </Grid>
          </Grid>
          <Grid className="explore-section" container>
            <Grid item xs={2}>
              <img
                src={WildlifeIcon}
                className="app-icon"
                alt="Wildlife Tourism"
              />
            </Grid>
            <Grid className="app-title" item xs={10}>
              Wildlife Tourism
            </Grid>
            <Grid item xs={12} className="app-description">
              Explore the intensity, distribution, and values of whale and
              dolphin watching in four Eastern Caribbean countries.
            </Grid>
            <Grid item xs={8} />
            <Grid item xs={4}>
              <Button
                className="app-button"
                variant="outlined"
                color="primary"
                onClick={() => setApp("wildlife-tourism")}
              >
                Explore 
                <ArrowForward />
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>
      <div className="content logos">
        <h2>Partners</h2>
        <div className="box">
          <img src={GEFlogo} className="small" alt="GEF" title="GEF" />
          <Divider className="divider" />
          <img src={WBGlogo} alt="World Bank Group" title="World Bank Group" />
          <Divider className="divider" />
          <img
            src={CROPlogo}
            className="medium"
            alt="Caribbean Regional Oceanscape Project"
            title="Caribbean Regional Oceanscape Project"
          />
          <Divider className="divider" />
          <img
            src={TNCLogo}
            alt="The Nature Conservancy"
            title="The Nature Conservancy"
          />
        </div>
      </div>
    </div>
  );
}

export default Intro;
